// BUILD-046 tasks 8.24 / 8.25: the action controls of ONE row - Assign to…, Recovery assignment…, Reassign…, Return to queue, Reopen (design
// "Oversight actions"). The same component serves the Oversight_View rows, the owner's own My tasks row and the requester's issued list, so the
// rules live in one place: which controls a row offers is oversightActionsModel.ts, how an action runs is taskActionsFlow.ts (both tested).
//
// Nothing is automatic and nothing is applied locally: a control opens a picker or a confirmation, the Caller confirms, one request is sent, and
// the host reloads the lists the change touched. Every refusal shows the server's message and leaves the row exactly as it was. Done and
// complete-on-behalf are not controls of this component, and it edits nobody's notes.
//
// Correction cycle 1bb88b3e (stage B2a, item N9): the row no longer shows a row of small buttons. Reopen (the one action a completed row is for) is a
// visible button; every other control is an entry of the row's ONE "More actions" menu (RowMoreMenu), together with the `extraEntries` the host row
// adds (Edit, Hand off). The picker or confirmation an entry opens appears below the row, as before. The row places the two parts itself through the
// `children` render function: `trigger` goes with the row's actions, `panel` goes below the row.
import { useEffect, useRef, useState } from 'react';
import { useAssignmentRequest } from './assignmentActionStore';
import type { ReactNode } from 'react';
import { notifySharedTasksChanged, onCallerChanged, workApi } from '../../api/workShared';
import { actionOutcomes } from './actionOutcomeStore';
import { describeControl, offeredControls } from './oversightActionsModel';
import type { ActionControl, ActionMe, ActionRow, ActionView } from './oversightActionsModel';
import { callerChangedFlow, closeControl, describeFlow, dismissOutcome, initialActionFlow, loadCandidates, openControl, performAction, selectTarget } from './taskActionsFlow';
import type { ActionFlowState, ActionRequest, ActionResponse, FlowStore } from './taskActionsFlow';
import { RowMoreMenu } from './RowMoreMenu';
import type { MenuEntry } from './RowMoreMenu';
// D1 display cleanup: the task title is shown (and named) without the "(FICTIONAL)" test marker; the people of the picker are First name + last initial, told apart by department.
import { disambiguatePeople, stripTestMarker } from './displayNames';

export interface TaskActionParts {
  /** The visible Reopen button and the "More actions" menu. Null when the row offers nothing (or while a picker is open). */
  trigger: ReactNode;
  /** The outcome message and the picker or confirmation. Null when there is nothing to show. */
  panel: ReactNode;
}

export interface TaskActionControlsProps {
  /** The Caller summary (`GET /work/me`). Without it nothing is offered. */
  me: ActionMe | null;
  row: ActionRow & { id: string; title?: string; ownerEmployeeId?: string };
  view: ActionView;
  /** An ACCEPTED action: the host reloads the lists the change touched (the task may have moved to another list). */
  onDone?: (done: { request: ActionRequest; response: ActionResponse }) => void;
  /** Entries the host row adds to the SAME menu (for example Edit and Hand off on My Work). */
  extraEntries?: readonly MenuEntry[];
  /** Places `trigger` and `panel`. Without it they are drawn one after the other. */
  children?: (parts: TaskActionParts) => ReactNode;
}

export function TaskActionControls({ me, row, view, onDone, extraEntries = [], children }: TaskActionControlsProps) {
  // Supplement 2026-10-24: the task's Edit form can ask this row to open its own Reassign flow. The row opens it as if its menu entry had been chosen, and only when the existing rules offer it.
  const askedReassign = useAssignmentRequest(row.id);
  const handledReassign = useRef(0);
  // The list reload that follows an accepted action draws this row again; its confirmation is remembered per task so the new row still shows it
  // (actionOutcomeMemory.ts, found by the browser run of task 9.39).
  const [state, setState] = useState<ActionFlowState>(() => {
    const remembered = actionOutcomes.recall(row.id);
    return remembered === null ? initialActionFlow() : { ...initialActionFlow(), outcome: remembered };
  });
  const latest = useRef<ActionFlowState>(state);
  const store = useRef<FlowStore>({
    get: () => latest.current,
    set: (next) => {
      latest.current = next;
      setState(next);
    },
  }).current;
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  // A Caller change drops whatever the previous Caller had open (nothing they chose is sent as the new Caller).
  useEffect(() => onCallerChanged(() => store.set(callerChangedFlow())), [store]);

  const decision = offeredControls(me, row, view);
  const owner = typeof row.ownerEmployeeId === 'string' ? row.ownerEmployeeId : '';
  const flow = describeFlow(state, { ownerEmployeeId: owner });
  const shownTitle = stripTestMarker(row.title);
  const title = shownTitle !== '' ? shownTitle : 'this task';
  // The people of the picker: First name + last initial, the department beside it, the full name where two would look alike (displayNames.disambiguatePeople).
  const optionChoices = disambiguatePeople(flow.options);

  function open(control: ActionControl): void {
    actionOutcomes.forget(row.id);
    store.set(openControl(latest.current, control));
    if (describeControl(control).needsTarget) void loadCandidates(workApi, store);
  }

  async function confirm(): Promise<void> {
    const outcome = await performAction(workApi, store, { taskId: row.id, ownerEmployeeId: owner });
    if (outcome.kind === 'done') {
      if (latest.current.outcome !== null) actionOutcomes.remember(row.id, latest.current.outcome, shownTitle, view);
      notifySharedTasksChanged({ reason: outcome.request.control, id: row.id });
      if (onDoneRef.current) onDoneRef.current({ request: outcome.request, response: outcome.response });
    }
  }

  // Owner direction 2026-10-23 (Assigned by Me): Reopen is an entry of the row's menu there, so a completed row is no taller than the others. Every other table keeps its visible Reopen button.
  useEffect(() => {
    if (askedReassign === null || askedReassign.control !== 'reassign' || askedReassign.nonce === handledReassign.current) return;
    handledReassign.current = askedReassign.nonce;
    const wanted = (['reassign', 'assign-to'] as ActionControl[]).find((control) => decision.controls.includes(control));
    if (wanted !== undefined) open(wanted);
  }, [askedReassign]); // eslint-disable-line react-hooks/exhaustive-deps

  const reopenOffered = decision.controls.includes('reopen') && view !== 'issued';
  const menuEntries: MenuEntry[] = [
    ...extraEntries,
    ...decision.controls
      .filter((control) => control !== 'reopen' || view === 'issued')
      .map((control): MenuEntry => ({ key: control, label: describeControl(control).label, ariaLabel: `${describeControl(control).label}: ${title}`, onSelect: () => open(control) })),
  ];

  const trigger: ReactNode =
    flow.open || (!reopenOffered && menuEntries.length === 0) ? null : (
      <>
        {reopenOffered && (
          <button
            type="button"
            onClick={() => open('reopen')}
            aria-label={`${describeControl('reopen').label}: ${title}`}
            className="rounded-md border border-[#2a7fb8] bg-white px-3 py-1.5 text-sm font-medium text-[#1f6fa5] hover:bg-sky-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
          >
            {describeControl('reopen').label}
          </button>
        )}
        <RowMoreMenu entries={menuEntries} taskTitle={title} />
      </>
    );

  const hasPanel = flow.outcome !== null || (flow.open && flow.control !== null);
  const panel: ReactNode = !hasPanel ? null : (
    <div className="mt-1 space-y-1 w-full" data-testid="task-action-controls">
      {flow.outcome !== null && (
        <p role="status" aria-live="polite" className="flex items-start justify-between gap-2 text-xs bg-green-50 text-green-900 border border-green-200 rounded px-2 py-1">
          <span>{flow.outcome}</span>
          <button
            type="button"
            onClick={() => {
              actionOutcomes.forget(row.id);
              store.set(dismissOutcome(latest.current));
            }}
            aria-label="Dismiss message"
            className="font-bold"
          >
            &times;
          </button>
        </p>
      )}

      {flow.open && flow.control !== null && (
        <div role="group" aria-label={`${describeControl(flow.control).label} ${title}`} className="border border-violet-200 bg-violet-50 rounded p-2 text-xs space-y-2">
          <p className="text-gray-800">{flow.prompt}</p>

          {flow.needsTarget && (
            <div>
              {flow.candidates.phase === 'loading' && (
                <p role="status" className="text-gray-600">
                  Loading people…
                </p>
              )}
              {flow.candidates.phase === 'error' && (
                <p role="alert" className="text-red-700">
                  Could not load people{flow.candidates.error !== null && flow.candidates.error.message !== '' ? `: ${flow.candidates.error.message}` : ''}.{' '}
                  <button type="button" onClick={() => void loadCandidates(workApi, store)} className="underline" aria-label="Retry loading people">
                    Retry
                  </button>
                </p>
              )}
              {flow.candidates.phase === 'empty' && <p className="text-gray-600">There is no one to choose from.</p>}
              {flow.candidates.phase === 'data' && (
                <>
                  <label htmlFor={`target-${row.id}`} className="block text-gray-700 mb-1">
                    Who should get it?
                  </label>
                  <select
                    id={`target-${row.id}`}
                    value={flow.selected}
                    disabled={flow.sending}
                    onChange={(event) => store.set(selectTarget(latest.current, event.target.value))}
                    className="w-full max-w-xs px-2 py-1 border border-gray-300 rounded text-xs bg-white"
                  >
                    <option value="">Choose a person…</option>
                    {flow.options.map((person, index) => (
                      <option key={person.employeeId} value={person.employeeId} title={optionChoices[index].accessibleName}>
                        {optionChoices[index].detail !== '' ? `${optionChoices[index].short} (${optionChoices[index].detail})` : optionChoices[index].short}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
          )}

          {flow.failure !== null && (
            <p role="alert" className="text-red-700">
              <strong>{flow.failure.headline}</strong>
              {flow.failure.detail !== null && <span className="ml-1">{flow.failure.detail}</span>}
            </p>
          )}

          <div className="flex items-center gap-2">
            <button type="button" disabled={flow.confirm.disabled} onClick={() => void confirm()} className="px-2 py-0.5 rounded bg-violet-600 text-white font-semibold hover:bg-violet-700 disabled:opacity-50">
              {flow.confirm.label}
            </button>
            <button type="button" disabled={flow.sending} onClick={() => store.set(closeControl(latest.current))} className="px-2 py-0.5 rounded border border-gray-300 bg-white disabled:opacity-50">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (children !== undefined) return <>{children({ trigger, panel })}</>;
  if (trigger === null && panel === null) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2">
      {trigger}
      {panel}
    </div>
  );
}
